import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  LinearProgress,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import DashboardCustomizeRoundedIcon from "@mui/icons-material/DashboardCustomizeRounded";
import MemoryRoundedIcon from "@mui/icons-material/MemoryRounded";
import SensorsRoundedIcon from "@mui/icons-material/SensorsRounded";
import QueryStatsRoundedIcon from "@mui/icons-material/QueryStatsRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import {
  ResponsiveContainer,
  LineChart,
  AreaChart,
  BarChart,
  Line,
  Area,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { useAuth } from "../../../auth/useAuth";
import { resolveDeviceStatus } from "../deviceStatus";
import {
  buildChartSeries,
  buildSchemaIndex,
  evaluateDerivedRule,
  evaluateFormula,
  evaluateThresholdStatus,
  formatMetricValue,
  getTrendLabel,
  normalizeDashboardConfig,
  normalizeSchemaDefinition,
  resolveOperationalMetricStatus,
  resolveUnit,
} from "./dashboardEngine";
import { resolveDeviceOperationalRoute } from "./dashboardProfiles";

import { APP_API_BASE } from "../../../config/api";
const API_BASE =
  process.env.REACT_APP_API_BASE_URL?.replace(/\/$/, "") ||
  `${APP_API_BASE}`;
const PALETTE = [
  "#2563EB",
  "#0F766E",
  "#7C3AED",
  "#EA580C",
  "#DC2626",
  "#059669",
];

const POLL_INTERVAL_MS = 30000;
const MAX_WIDGETS_RENDERED = 12;

function WidgetTooltip({ active, payload, label, unit }) {
  if (!active || !payload?.length) return null;

  return (
    <Box
      sx={{
        px: 1.5,
        py: 1.1,
        borderRadius: 2.5,
        border: "1px solid rgba(148,163,184,0.22)",
        backgroundColor: "rgba(15,23,42,0.92)",
        backdropFilter: "blur(10px)",
        boxShadow: "0 16px 32px rgba(15,23,42,0.22)",
      }}
    >
      <Typography
        sx={{ fontSize: 11, color: "rgba(226,232,240,0.72)", mb: 0.4 }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontSize: 14, fontWeight: 800, color: "#F8FAFC" }}>
        {payload[0]?.value ?? "--"}
        {unit ? ` ${unit}` : ""}
      </Typography>
    </Box>
  );
}

function DeviceWidgetChart({ widget }) {
  const data = widget.chartData || [];

  if (!data.length) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#94A3B8",
          fontSize: 13,
        }}
      >
        No data is available for the selected period.
      </Box>
    );
  }

  const sharedProps = {
    data,
    margin: { top: 8, right: 4, left: -26, bottom: 0 },
  };
  const gradientId = `widget-gradient-${widget.id}`;
  const axes = (
    <>
      <CartesianGrid
        strokeDasharray="3 4"
        vertical={false}
        stroke="rgba(148,163,184,0.22)"
      />
      <XAxis
        dataKey="time"
        tick={{ fontSize: 11, fill: "#94A3B8" }}
        tickLine={false}
        axisLine={false}
      />
      <YAxis
        tick={{ fontSize: 11, fill: "#94A3B8" }}
        tickLine={false}
        axisLine={false}
        width={34}
      />
      <Tooltip
        content={<WidgetTooltip unit={widget.unit} />}
        cursor={{ stroke: "rgba(148,163,184,0.24)", strokeWidth: 1 }}
      />
    </>
  );

  if (widget.widgetType === "area") {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart {...sharedProps}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={widget.color} stopOpacity={0.28} />
              <stop offset="100%" stopColor={widget.color} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          {axes}
          <Area
            type="monotone"
            dataKey="value"
            stroke={widget.color}
            fill={`url(#${gradientId})`}
            strokeWidth={3}
          />
        </AreaChart>
      </ResponsiveContainer>
    );
  }

  if (widget.widgetType === "bar") {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart {...sharedProps}>
          {axes}
          <Bar dataKey="value" fill={widget.color} radius={[6, 6, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart {...sharedProps}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={widget.color} stopOpacity={0.18} />
            <stop offset="100%" stopColor={widget.color} stopOpacity={0.02} />
          </linearGradient>
        </defs>
        {axes}
        <Area
          type="monotone"
          dataKey="value"
          stroke="none"
          fill={`url(#${gradientId})`}
        />
        <Line
          type="monotone"
          dataKey="value"
          stroke={widget.color}
          strokeWidth={3}
          dot={false}
          activeDot={{ r: 5, fill: widget.color, stroke: "#fff", strokeWidth: 2 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

function DeviceTypeDashboardSkeleton() {
  const statCards = Array.from({ length: 4 });
  const fleetCards = Array.from({ length: 4 });
  const widgetCards = Array.from({ length: 4 });

  return (
    <Box
      sx={{
        minHeight: "100%",
        background:
          "radial-gradient(circle at top left, rgba(14, 165, 233, 0.14), transparent 30%), radial-gradient(circle at top right, rgba(16, 185, 129, 0.12), transparent 28%), linear-gradient(180deg, #F8FBFF 0%, #EEF4F8 100%)",
        py: 1,
        pt: { xs: 5, sm: 5, md: 3 },
        pb: 4,
        px: { xs: 2, md: 3 },
      }}
    >
      <Box sx={{ maxWidth: 1440, mx: "auto" }}>
        <Card
          sx={{
            mb: 3,
            borderRadius: 6,
            overflow: "hidden",
            border: "1px solid rgba(148, 163, 184, 0.18)",
            background:
              "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
            boxShadow: "0 32px 80px rgba(15,23,42,0.18)",
          }}
        >
          <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
            <Stack
              direction={{ xs: "column", lg: "row" }}
              spacing={3}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", lg: "flex-start" }}
            >
              <Box sx={{ flex: 1, width: "100%" }}>
                <Skeleton
                  variant="rounded"
                  width={170}
                  height={36}
                  sx={{ mb: 2, borderRadius: 999, bgcolor: "rgba(255,255,255,0.18)" }}
                />
                <Skeleton
                  variant="rounded"
                  width={230}
                  height={30}
                  sx={{ mb: 1.5, borderRadius: 999, bgcolor: "rgba(255,255,255,0.16)" }}
                />
                <Skeleton
                  variant="text"
                  width="58%"
                  height={58}
                  sx={{ bgcolor: "rgba(255,255,255,0.2)" }}
                />
                <Skeleton
                  variant="text"
                  width="42%"
                  height={24}
                  sx={{ bgcolor: "rgba(255,255,255,0.16)" }}
                />
                <Skeleton
                  variant="text"
                  width="50%"
                  height={24}
                  sx={{ bgcolor: "rgba(255,255,255,0.14)" }}
                />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr 1fr", lg: "160px 160px" },
                  gap: 1.25,
                  flexShrink: 0,
                  width: { xs: "100%", lg: "auto" },
                }}
              >
                {statCards.map((_, index) => (
                  <Card
                    key={index}
                    sx={{
                      borderRadius: 3,
                      backgroundColor: "rgba(255,255,255,0.08)",
                      border: "1px solid rgba(255,255,255,0.10)",
                      boxShadow: "none",
                    }}
                  >
                    <CardContent sx={{ p: "14px 16px !important" }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box sx={{ width: "68%" }}>
                          <Skeleton
                            variant="text"
                            width="100%"
                            height={18}
                            sx={{ bgcolor: "rgba(255,255,255,0.16)" }}
                          />
                          <Skeleton
                            variant="text"
                            width="48%"
                            height={34}
                            sx={{ bgcolor: "rgba(255,255,255,0.2)" }}
                          />
                        </Box>
                        <Skeleton
                          variant="circular"
                          width={36}
                          height={36}
                          sx={{ bgcolor: "rgba(255,255,255,0.2)" }}
                        />
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Stack spacing={2.5}>
          <Card
            sx={{
              borderRadius: 5,
              border: "1px solid rgba(15,23,42,0.08)",
              boxShadow: "0 18px 40px rgba(15,23,42,0.08)",
            }}
          >
            <CardHeader
              title={<Skeleton variant="text" width={120} height={28} sx={{ bgcolor: "rgba(255,255,255,0.18)" }} />}
              sx={{
                background:
                  "linear-gradient(135deg, rgba(15,23,42,0.96), rgba(15,118,110,0.88) 55%, rgba(8,47,73,0.92))",
              }}
            />
            <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1.25,
                  "& > *": {
                    flex: { xs: "1 1 100%", md: "1 1 45%", xl: "1 1 20%" },
                    minWidth: 0,
                  },
                }}
              >
                {fleetCards.map((_, index) => (
                  <Box
                    key={index}
                    sx={{
                      p: 1.5,
                      borderRadius: 3.5,
                      border: "1px solid rgba(226,232,240,0.9)",
                      background: "#fff",
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                      <Box sx={{ width: "70%" }}>
                        <Skeleton variant="text" width="86%" height={26} />
                        <Skeleton variant="text" width="64%" height={20} />
                      </Box>
                      <Skeleton variant="rounded" width={76} height={24} sx={{ borderRadius: 999 }} />
                    </Stack>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>

          <Card
            sx={{
              borderRadius: 5,
              border: "1px solid rgba(15,23,42,0.08)",
              boxShadow: "0 18px 40px rgba(15,23,42,0.08)",
              overflow: "hidden",
            }}
          >
            <CardHeader
              title={
                <Stack direction="row" spacing={1} alignItems="center">
                  <Skeleton variant="text" width={180} height={30} sx={{ bgcolor: "rgba(255,255,255,0.18)" }} />
                  <Skeleton variant="rounded" width={76} height={24} sx={{ borderRadius: 999, bgcolor: "rgba(255,255,255,0.18)" }} />
                </Stack>
              }
              subheader={<Skeleton variant="text" width={220} height={22} sx={{ bgcolor: "rgba(255,255,255,0.14)" }} />}
              action={
                <Stack direction="row" spacing={1}>
                  <Skeleton variant="rounded" width={150} height={32} sx={{ borderRadius: 999, bgcolor: "rgba(255,255,255,0.18)" }} />
                  <Skeleton variant="rounded" width={112} height={36} sx={{ borderRadius: 999, bgcolor: "rgba(255,255,255,0.18)" }} />
                </Stack>
              }
              sx={{
                borderBottom: "1px solid rgba(226,232,240,0.8)",
                background:
                  "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
                px: { xs: 2, sm: 2.5 },
                py: { xs: 1.8, sm: 2.2 },
              }}
            />
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ px: 2.5, py: 2, borderBottom: "1px solid rgba(226,232,240,0.8)" }}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {fleetCards.map((_, index) => (
                    <Skeleton key={index} variant="rounded" width={110} height={36} sx={{ borderRadius: 999 }} />
                  ))}
                </Stack>
              </Box>

              <Box sx={{ p: 2.5 }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
                    gap: 1.5,
                  }}
                >
                  {widgetCards.map((_, index) => (
                    <Box
                      key={index}
                      sx={{
                        p: 2,
                        borderRadius: 5,
                        border: "1px solid rgba(148,163,184,0.16)",
                        background:
                          "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)",
                        boxShadow: "0 18px 38px rgba(15,23,42,0.06)",
                        gridColumn: index === 0 ? { xs: "span 1", md: "span 2" } : "span 1",
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                        <Box sx={{ width: "70%" }}>
                          <Skeleton variant="text" width="34%" height={18} />
                          <Skeleton variant="text" width="72%" height={34} />
                        </Box>
                        <Skeleton variant="rounded" width={84} height={24} sx={{ borderRadius: 999 }} />
                      </Stack>
                      <Skeleton variant="text" width="38%" height={54} sx={{ mt: 2 }} />
                      <Skeleton variant="text" width="46%" height={22} />
                      <Skeleton variant="text" width="58%" height={20} />
                      <Skeleton variant="rounded" width="100%" height={190} sx={{ mt: 2.5, borderRadius: 4 }} />
                    </Box>
                  ))}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Stack>
      </Box>
    </Box>
  );
}

function DeviceTypeDashboard() {
  const navigate = useNavigate();
  const { deviceTypeId } = useParams();
  const { activeOrg } = useAuth();

  const [loading, setLoading] = useState(true);
  const [deviceType, setDeviceType] = useState(null);
  const [fleetDevices, setFleetDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState("");
  const [telemetryByDeviceId, setTelemetryByDeviceId] = useState({});
  const [loadingTelemetry, setLoadingTelemetry] = useState(false);
  const [telemetryError, setTelemetryError] = useState("");
  const [isPageVisible, setIsPageVisible] = useState(
    typeof document === "undefined" ? true : document.visibilityState === "visible",
  );

  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const handleVisibilityChange = () => {
      setIsPageVisible(document.visibilityState === "visible");
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  useEffect(() => {
    if (!deviceTypeId) return;

    const fetchDashboard = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("authToken");
        if (!token) return;

        const headers = {
          Authorization: `Bearer ${token}`,
          ...(activeOrg?.id && { "X-Org-ID": activeOrg.id }),
        };

                const [typeRes, devicesRes] = await Promise.all([
          axios.get(`${API_BASE}/api/v1/device-types/${deviceTypeId}`, {
            headers,
          }),
          axios.get(`${API_BASE}/api/v1/devices`, {
            headers,
            params: { view: "summary" },
          }),
        ]);

        const typePayload = typeRes.data || null;
        const allDevices = Array.isArray(devicesRes.data)
          ? devicesRes.data
          : [];
        const matchedDevices = allDevices.filter(
          (device) =>
            device.device_type?.id === deviceTypeId ||
            device.device_type_id === deviceTypeId,
        );

        setDeviceType(typePayload);
        setFleetDevices(matchedDevices);
        setTelemetryByDeviceId({});
        setSelectedDeviceId((current) =>
          current &&
            matchedDevices.some((device) => device.id === current)
            ? current
            : matchedDevices[0]?.id || "",
        );
      } catch (error) {
        console.error("Failed to load device type dashboard", error);
        setDeviceType(null);
        setFleetDevices([]);
        setSelectedDeviceId("");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [deviceTypeId, activeOrg?.id]);

  const fetchTelemetryForDevice = useCallback(
    async (deviceId, silent = false) => {
      if (!deviceId) return;

      const token = localStorage.getItem("authToken");
      if (!token) return;

      const headers = {
        Authorization: `Bearer ${token}`,
        ...(activeOrg?.id && { "X-Org-ID": activeOrg.id }),
      };

      try {
        if (!silent) {
          setLoadingTelemetry(true);
        }

        setTelemetryError("");
        const telemetryRes = await axios.get(
          `${API_BASE}/api/v1/devices/${deviceId}/telemetry`,
          {
            headers,
            params: {
              limit: 240,
              include_total: "false",
            },
          },
        );

        const rows = Array.isArray(telemetryRes.data?.data)
          ? telemetryRes.data.data.map((row) => ({
            timestamp: row.timestamp,
            server_time: row.server_time,
            ...row.data,
          }))
          : [];

        setTelemetryByDeviceId((previous) => ({
          ...previous,
          [deviceId]: {
            rows,
            loadedAt: new Date().toISOString(),
          },
        }));
      } catch (error) {
        setTelemetryError("Unable to refresh telemetry. Showing last successful data.");
      } finally {
        if (!silent) {
          setLoadingTelemetry(false);
        }
      }
    },
    [activeOrg?.id],
  );

  useEffect(() => {
    if (!selectedDeviceId) return;
    fetchTelemetryForDevice(selectedDeviceId);
  }, [selectedDeviceId, fetchTelemetryForDevice]);

  useEffect(() => {
    if (!selectedDeviceId || !isPageVisible) return;

    const timer = setInterval(() => {
      fetchTelemetryForDevice(selectedDeviceId, true);
    }, POLL_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [selectedDeviceId, isPageVisible, fetchTelemetryForDevice]);

  const schema = useMemo(
    () => normalizeSchemaDefinition(deviceType?.config?.schema_definition || []),
    [deviceType?.config?.schema_definition],
  );
  const schemaIndex = useMemo(() => buildSchemaIndex(schema), [schema]);

  const dashboardConfig = useMemo(
    () =>
      Array.isArray(deviceType?.config?.dashboard_config)
        ? deviceType.config.dashboard_config
        : [],
    [deviceType?.config?.dashboard_config],
  );

  const thresholdRules = useMemo(
    () =>
      Array.isArray(deviceType?.config?.threshold_rules)
        ? deviceType.config.threshold_rules
        : [],
    [deviceType?.config?.threshold_rules],
  );

  const businessRules = useMemo(
    () =>
      Array.isArray(deviceType?.config?.business_rules)
        ? deviceType.config.business_rules
        : [],
    [deviceType?.config?.business_rules],
  );

  const normalizedDashboard = useMemo(
    () => normalizeDashboardConfig(dashboardConfig, schema),
    [dashboardConfig, schema],
  );

  const effectiveDashboardConfig = useMemo(
    () => normalizedDashboard.widgets || [],
    [normalizedDashboard.widgets],
  );

  const fleetCards = useMemo(() => {
    return fleetDevices.map((device, deviceIndex) => {
      const telemetryRows = telemetryByDeviceId[device.id]?.rows || [];
      const latestPoint = telemetryRows.length ? telemetryRows[0] : null;
      const deviceStatus = resolveDeviceStatus(device);

      const widgets = effectiveDashboardConfig
        .slice(0, MAX_WIDGETS_RENDERED)
        .map((widget, index) => {
          const field =
            schemaIndex[String(widget.metric_key || "").toLowerCase()] || {};

          const formula = String(widget.formula || "").trim();
          const formulaResult = formula
            ? evaluateFormula(formula, latestPoint || {})
            : { value: null, missingInputs: [] };

          const rawValue =
            formula && formulaResult.value !== null
              ? formulaResult.value
              : latestPoint?.[widget.metric_key];

          const unit = widget.unit || resolveUnit(field);
          const chartData = buildChartSeries(telemetryRows, widget.metric_key, 24);

          return {
            id: `${device.id}-${widget.metric_key || "widget"}-${index}`,
            title: widget.title || field.label || widget.metric_key || `Widget ${index + 1}`,
            metricKey: widget.metric_key,
            widgetType: widget.widget_type || "stat",
            size: widget.size || "md",
            color: PALETTE[(deviceIndex + index) % PALETTE.length],
            unit,
            value: formatMetricValue(rawValue, unit),
            rawValue,
            chartData,
            trendLabel: getTrendLabel(chartData),
            status: resolveOperationalMetricStatus(
              deviceStatus,
              evaluateThresholdStatus(
                thresholdRules,
                widget.metric_key,
                rawValue,
              ),
              Boolean(latestPoint),
            ),
            formulaMissingInputs: formulaResult.missingInputs || [],
          };
        });

      return {
        ...device,
        telemetryRows,
        telemetryLoadedAt: telemetryByDeviceId[device.id]?.loadedAt || null,
        widgets,
        status: deviceStatus,
      };
    });
  }, [
    fleetDevices,
    effectiveDashboardConfig,
    schemaIndex,
    telemetryByDeviceId,
    thresholdRules,
  ]);

  const overviewStats = useMemo(
    () => ({
      totalDevices: fleetCards.length,
      onlineDevices: fleetCards.filter(
        (item) => item.status?.status === "online",
      ).length,
      staleDevices: fleetCards.filter(
        (item) => item.status?.status === "stale",
      ).length,
      offlineDevices: fleetCards.filter(
        (item) => item.status?.status === "offline",
      ).length,
      configuredWidgets: effectiveDashboardConfig.length,
      telemetryFeeds: fleetCards.filter(
        (item) => item.telemetryRows?.length > 0,
      ).length,
    }),
    [fleetCards, effectiveDashboardConfig.length],
  );

  const selectedDevice = useMemo(
    () =>
      fleetCards.find((device) => device.id === selectedDeviceId) ||
      fleetCards[0] ||
      null,
    [fleetCards, selectedDeviceId],
  );

  const evaluatedBusinessRules = useMemo(() => {
    const latestPoint = selectedDevice?.telemetryRows?.length
      ? selectedDevice.telemetryRows[0]
      : null;

    if (!latestPoint || !Array.isArray(businessRules) || !businessRules.length) {
      return [];
    }

    return businessRules.map((rule, index) => ({
      ...evaluateDerivedRule(rule, latestPoint),
      id: `${selectedDevice?.id || "device"}-rule-${index}`,
      description: rule?.description || "",
    }));
  }, [businessRules, selectedDevice]);
  const rawTelemetryEntries = useMemo(() => {
    const latestPoint = selectedDevice?.telemetryRows?.[0];
    if (!latestPoint || typeof latestPoint !== "object") {
      return [];
    }

    return Object.entries(latestPoint)
      .filter(([key]) => key !== "timestamp" && key !== "server_time")
      .slice(0, 60);
  }, [selectedDevice]);

  if (loading) {
    return <DeviceTypeDashboardSkeleton />;
  }

  return (
    <Box
      sx={{
        minHeight: "100%",
        background:
          "radial-gradient(circle at top left, rgba(14, 165, 233, 0.14), transparent 30%), radial-gradient(circle at top right, rgba(16, 185, 129, 0.12), transparent 28%), linear-gradient(180deg, #F8FBFF 0%, #EEF4F8 100%)",
        py: 1,
        pt: { xs: 5, sm: 5, md: 3 },
        pb: 4,
        px: { xs: 2, md: 3 },
      }}
    >
      <Box sx={{ maxWidth: 1440, mx: "auto" }}>
        <Card
          sx={{
            mb: 3,
            borderRadius: 6,
            overflow: "hidden",
            border: "1px solid rgba(148, 163, 184, 0.18)",
            background:
              "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
            boxShadow: "0 32px 80px rgba(15,23,42,0.18)",
          }}
        >
          <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
            <Stack
              direction={{ xs: "column", lg: "row" }}
              spacing={3}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", lg: "flex-start" }}
            >
              {/* LEFT */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Button
                  startIcon={<ArrowBackRoundedIcon />}
                  onClick={() => navigate("/device_onboarding/devicetypes")}
                  sx={{
                    mb: 2,
                    color: "#E2E8F0",
                    textTransform: "none",
                    borderRadius: 999,
                    border: "1px solid rgba(255,255,255,0.14)",
                    backgroundColor: "rgba(255,255,255,0.06)",
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.10)" },
                  }}
                >
                  Back to device types
                </Button>
                <Chip
                  label="Custom Device Family Dashboard"
                  sx={{
                    mb: 1.5,
                    display: "flex",
                    width: "fit-content",
                    color: "#DBEAFE",
                    backgroundColor: "rgba(255,255,255,0.08)",
                    border: "1px solid rgba(255,255,255,0.12)",
                    fontWeight: 700,
                  }}
                />
                <Typography
                  sx={{
                    color: "#F8FAFC",
                    fontSize: { xs: 26, sm: 34, md: 44 },
                    fontWeight: 800,
                    letterSpacing: "-0.04em",
                    lineHeight: 1.1,
                  }}
                >
                  {deviceType?.name || "Device Type Dashboard"}
                </Typography>
                <Typography
                  sx={{
                    mt: 1,
                    color: "rgba(226,232,240,0.82)",
                    maxWidth: 560,
                    lineHeight: 1.7,
                    fontSize: 14,
                  }}
                >
                  Premium fleet view for every device mapped to this device type,
                  driven by your schema and dashboard configuration.
                </Typography>
              </Box>

              {/* RIGHT — 2×2 grid of stat cards */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr 1fr", lg: "160px 160px" },
                  gap: 1.25,
                  flexShrink: 0,
                  width: { xs: "100%", lg: "auto" },
                }}
              >
                {[
                  {
                    label: "Linked devices",
                    value: overviewStats.totalDevices,
                    icon: <MemoryRoundedIcon fontSize="small" />,
                  },
                  {
                    label: "Online",
                    value: overviewStats.onlineDevices,
                    icon: <QueryStatsRoundedIcon fontSize="small" />,
                  },
                  {
                    label: "Stale",
                    value: overviewStats.staleDevices,
                    icon: <SensorsRoundedIcon fontSize="small" />,
                  },
                  {
                    label: "Widgets",
                    value: overviewStats.configuredWidgets,
                    icon: <DashboardCustomizeRoundedIcon fontSize="small" />,
                  },
                ].map((item) => (
                  <Card
                    key={item.label}
                    sx={{
                      borderRadius: 3,
                      color: "#F8FAFC",
                      backgroundColor: "rgba(255,255,255,0.08)",
                      border: "1px solid rgba(255,255,255,0.10)",
                      boxShadow: "none",
                    }}
                  >
                    <CardContent sx={{ p: "14px 16px !important" }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography
                            sx={{
                              fontSize: 11,
                              color: "rgba(226,232,240,0.72)",
                              fontWeight: 700,
                              letterSpacing: "0.03em",
                            }}
                          >
                            {item.label}
                          </Typography>
                          <Typography sx={{ mt: 0.5, fontSize: 24, fontWeight: 800, lineHeight: 1 }}>
                            {item.value}
                          </Typography>
                        </Box>
                        <Avatar
                          sx={{
                            width: 36,
                            height: 36,
                            bgcolor: "rgba(255,255,255,0.85)",
                            color: "#0F4C81",
                            flexShrink: 0,
                          }}
                        >
                          {item.icon}
                        </Avatar>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {fleetCards.length === 0 ? (
          <Card
            sx={{ borderRadius: 5, border: "1px solid rgba(15,23,42,0.08)" }}
          >
            <CardContent sx={{ p: 4 }}>
              <Typography
                sx={{ fontSize: 24, fontWeight: 800, color: "#0F172A" }}
              >
                No devices linked to this type yet
              </Typography>
              <Typography sx={{ mt: 1, color: "#64748B" }}>
                Once devices are assigned to this type, this dashboard will show
                their configured KPIs and trend charts here.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Stack spacing={2.5}>
            <Card
              sx={{
                borderRadius: 5,
                border: "1px solid rgba(15,23,42,0.08)",
                boxShadow: "0 18px 40px rgba(15,23,42,0.08)",
              }}
            >
              <CardHeader
                title="Fleet Status"
                sx={{
                  background: "linear-gradient(135deg, rgba(15,23,42,0.96), rgba(15,118,110,0.88) 55%, rgba(8,47,73,0.92))",
                  "& .MuiCardHeader-title": {
                    fontSize: 16,
                    fontWeight: 800,
                    color: "#fff",
                  },
                }}
              />
              <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1.25,
                    "& > *": {
                      flex: {
                        xs: "1 1 100%",
                        md: "1 1 45%",
                        xl: "1 1 20%",
                      },
                      minWidth: 0,
                    },
                  }}
                >
                  {fleetCards.map((device) => (
                    <Box
                      key={device.id}
                      onClick={() => setSelectedDeviceId(device.id)}
                      sx={{
                        p: 1.5,
                        borderRadius: 3.5,
                        cursor: "pointer",
                        border:
                          selectedDevice?.id === device.id
                            ? "1px solid rgba(37,99,235,0.35)"
                            : "1px solid rgba(226,232,240,0.9)",
                        background:
                          selectedDevice?.id === device.id
                            ? "linear-gradient(180deg, rgba(239,246,255,0.95), rgba(248,250,252,0.98))"
                            : "#fff",
                        boxShadow:
                          selectedDevice?.id === device.id
                            ? "0 12px 24px rgba(37,99,235,0.10)"
                            : "none",
                        transition: "all 160ms ease",
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        spacing={1}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: 16,
                              fontWeight: 800,
                              color: "#0F172A",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {device.name}
                          </Typography>
                          <Typography
                            sx={{ mt: 0.5, color: "#64748B", fontSize: 13 }}
                          >
                            {device.last_seen ? `Status: ${device.status?.label || "Unknown"}` : "No telemetry has been received."}
                          </Typography>
                        </Box>
                        <Chip
                          label={device.status?.label || "Unknown"}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            color: device.status?.color || "#475569",
                            backgroundColor: `${device.status?.color || "#475569"}1A`,
                          }}
                        />
                      </Stack>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>

            <Card
              sx={{
                borderRadius: 5,
                border: "1px solid rgba(15,23,42,0.08)",
                boxShadow: "0 18px 40px rgba(15,23,42,0.08)",
                overflow: "hidden",
              }}
            >
              <CardHeader
                sx={{
                  borderBottom: "1px solid rgba(226,232,240,0.8)",
                  background:
                    "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
                  px: { xs: 2, sm: 2.5 },
                  py: { xs: 1.8, sm: 2.2 },
                }}
                title={
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    sx={{ width: "100%" }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: 16, sm: 18, md: 20 },
                        fontWeight: 800,
                        color: "#fff",
                        wordBreak: "break-word",
                      }}
                    >
                      {selectedDevice?.name}
                    </Typography>

                    <Chip
                      label={selectedDevice?.status?.label || "Unknown"}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        color: selectedDevice?.status?.color || "#475569",
                        backgroundColor: `${selectedDevice?.status?.color || "#475569"}1A`,
                        alignSelf: { xs: "flex-start", sm: "center" },
                      }}
                    />
                  </Stack>
                }
                subheader={
                  <Typography
                    sx={{
                      mt: 0.75,
                      color: "#cac4c4",
                      fontSize: { xs: 12, sm: 13, md: 14 },
                      lineHeight: 1.4,
                    }}
                  >
                    {selectedDevice?.telemetryRows?.length
                      ? `${selectedDevice.telemetryRows.length} telemetry points loaded`
                      : "No telemetry has been received."}
                  </Typography>
                }
                action={
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    alignItems={{ xs: "stretch", sm: "center" }}
                    sx={{
                      mt: { xs: 1, sm: 0 },
                      width: { xs: "100%", sm: "auto" },
                    }}
                  >
                    <Chip
                      label={`${selectedDevice?.widgets?.length || 0} configured widgets`}
                      sx={{
                        fontWeight: 700,
                        color: "#0F4C81",
                        backgroundColor: "#EFF6FF",
                        width: { xs: "100%", sm: "auto" },
                        textAlign: "center",
                      }}
                    />

                    <Button
                      variant="outlined"
                      endIcon={<OpenInNewRoundedIcon />}
                      onClick={() => {
                        if (!selectedDevice) return;

                        const route = resolveDeviceOperationalRoute({
                          device: selectedDevice,
                          deviceType,
                        });

                        navigate(route, {
                          state: { device: selectedDevice, deviceType },
                        });
                      }}
                      sx={{
                        borderRadius: 999,
                        textTransform: "none",
                        fontWeight: 700,
                        color: "#dfdddd",
                        width: { xs: "100%", sm: "auto" },
                        justifyContent: "center",
                      }}
                    >
                      View device
                    </Button>
                  </Stack>
                }
              />
              {loadingTelemetry ? <LinearProgress /> : null}
              <CardContent sx={{ p: 0 }}>
                <Box
                  sx={{
                    px: 2.5,
                    py: 2,
                    borderBottom: "1px solid rgba(226,232,240,0.8)",
                  }}
                >
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {fleetCards.map((device) => (
                      <Button
                        key={device.id}
                        onClick={() => setSelectedDeviceId(device.id)}
                        variant={
                          selectedDevice?.id === device.id
                            ? "contained"
                            : "outlined"
                        }
                        sx={{
                          textTransform: "none",
                          borderRadius: 999,
                          fontWeight: 700,
                          px: 1.6,
                          py: 0.8,
                          backgroundColor:
                            selectedDevice?.id === device.id
                              ? "#0F172A"
                              : "#fff",
                          color:
                            selectedDevice?.id === device.id
                              ? "#fff"
                              : "#0F172A",
                          borderColor: "rgba(15,23,42,0.12)",
                          "&:hover": {
                            backgroundColor:
                              selectedDevice?.id === device.id
                                ? "#111827"
                                : "#F8FAFC",
                          },
                        }}
                      >
                        {device.name}
                      </Button>
                    ))}
                  </Stack>
                </Box>

                <Box sx={{ p: 2.5 }}>
                  {normalizedDashboard.errors?.length ? (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
                        Widget configuration issues detected
                      </Typography>
                      <Typography sx={{ fontSize: 13 }}>
                        {normalizedDashboard.errors[0]}
                        {normalizedDashboard.errors.length > 1
                          ? ` (+${normalizedDashboard.errors.length - 1} more)`
                          : ""}
                      </Typography>
                    </Alert>
                  ) : null}

                  {telemetryError ? (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      {telemetryError}
                    </Alert>
                  ) : null}

                  {selectedDevice?.status?.status !== "online" &&
                  selectedDevice?.telemetryRows?.length ? (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Showing last known values from {selectedDevice.status.label.toLowerCase()}.
                    </Alert>
                  ) : null}
                  {evaluatedBusinessRules.length > 0 ? (
                    <Box sx={{ mb: 2.5 }}>
                      <Typography
                        sx={{
                          fontSize: 18,
                          fontWeight: 800,
                          color: "#0F172A",
                          mb: 1.5,
                        }}
                      >
                        Business Logic Signals
                      </Typography>

                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "1fr",
                            md: "repeat(2, minmax(0, 1fr))",
                          },
                          gap: 1.5,
                        }}
                      >
                        {evaluatedBusinessRules.map((rule, index) => (
                          <Box
                            key={rule.id}
                            sx={{
                              p: 2,
                              borderRadius: 4,
                              border: "1px solid rgba(79, 70, 229, 0.12)",
                              background:
                                "linear-gradient(180deg, rgba(238,242,255,0.9) 0%, rgba(255,255,255,0.98) 100%)",
                              boxShadow: "0 14px 30px rgba(79,70,229,0.08)",
                            }}
                          >
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="flex-start"
                              spacing={1}
                            >
                              <Box>
                                <Typography
                                  sx={{
                                    fontSize: 11,
                                    color: "#6366F1",
                                    fontWeight: 800,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.12em",
                                  }}
                                >
                                  Derived Metric
                                </Typography>
                                <Typography
                                  sx={{
                                    mt: 1,
                                    fontSize: 22,
                                    fontWeight: 800,
                                    color: "#111827",
                                    letterSpacing: "-0.02em",
                                  }}
                                >
                                  {rule.name}
                                </Typography>
                              </Box>
                              <Chip
                                label={rule.targetKey || `rule_${index + 1}`}
                                size="small"
                                sx={{
                                  fontWeight: 700,
                                  color: "#4338CA",
                                  backgroundColor: "#E0E7FF",
                                }}
                              />
                            </Stack>

                            <Typography
                              sx={{
                                mt: 2.5,
                                fontSize: 34,
                                fontWeight: 900,
                                color: "#312E81",
                                letterSpacing: "-0.04em",
                                lineHeight: 1,
                              }}
                            >
                              {rule.value === null || rule.value === undefined ? "--" : Number(rule.value).toFixed(2)}
                            </Typography>

                            <Typography
                              sx={{
                                mt: 1.25,
                                color: "#6366F1",
                                fontSize: 12,
                                fontWeight: 700,
                              }}
                            >
                              Formula: {rule.formula}
                            </Typography>

                            {rule.error ? (
                              <Typography
                                sx={{
                                  mt: 0.75,
                                  color: "#6B7280",
                                  fontSize: 12.5,
                                }}
                              >
                                {rule.error}
                              </Typography>
                            ) : null}

                            {rule.description ? (
                              <Typography
                                sx={{
                                  mt: 0.75,
                                  color: "#64748B",
                                  fontSize: 13,
                                  lineHeight: 1.65,
                                }}
                              >
                                {rule.description}
                              </Typography>
                            ) : null}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ) : null}

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        md: "repeat(2, minmax(0, 1fr))",
                      },
                      gap: 1.5,
                    }}
                  >
                    {(selectedDevice?.widgets || []).map((widget) => (
                      <Box
                        key={widget.id}
                        sx={{
                          p: 2,
                          borderRadius: 5,
                          border: "1px solid rgba(148,163,184,0.16)",
                          background:
                            "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)",
                          boxShadow: "0 18px 38px rgba(15,23,42,0.06)",
                          gridColumn:
                            widget.size === "lg"
                              ? { xs: "span 1", md: "span 2" }
                              : "span 1",
                          position: "relative",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            position: "absolute",
                            inset: "0 auto auto 0",
                            width: "100%",
                            height: 4,
                            background: `linear-gradient(90deg, ${widget.color}, transparent)`,
                            opacity: 0.95,
                          }}
                        />
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="flex-start"
                          spacing={1}
                        >
                          <Box>
                            <Typography
                              sx={{
                                fontSize: 11,
                                color: "#64748B",
                                fontWeight: 800,
                                textTransform: "uppercase",
                                letterSpacing: "0.12em",
                              }}
                            >
                              {widget.widgetType}
                            </Typography>
                            <Typography
                              sx={{
                                mt: 1,
                                fontSize: 22,
                                fontWeight: 800,
                                color: "#0F172A",
                                letterSpacing: "-0.02em",
                              }}
                            >
                              {widget.title}
                            </Typography>
                          </Box>
                          <Chip
                            label={widget.status.label}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              color: widget.status.tone,
                              backgroundColor: widget.status.bg,
                              borderRadius: 999,
                            }}
                          />
                        </Stack>

                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="flex-end"
                          spacing={2}
                          sx={{ mt: 2.5 }}
                        >
                          <Typography
                            sx={{
                              fontSize: 38,
                              fontWeight: 900,
                              color: "#111827",
                              letterSpacing: "-0.05em",
                              lineHeight: 1,
                            }}
                          >
                            {widget.value}
                          </Typography>
                          <Chip
                            label={widget.trendLabel || getTrendLabel(widget.chartData)}
                            size="small"
                            sx={{
                              alignSelf: "center",
                              fontWeight: 800,
                              color: widget.color,
                              backgroundColor: `${widget.color}14`,
                              border: `1px solid ${widget.color}22`,
                            }}
                          />
                        </Stack>

                        <Typography
                          sx={{
                            mt: 1,
                            color: "#64748B",
                            fontSize: 13,
                            fontWeight: 600,
                          }}
                        >
                          Metric key: {widget.metricKey}
                        </Typography>
                        <Typography
                          sx={{ mt: 0.4, color: "#94A3B8", fontSize: 12 }}
                        >
                          Latest operational reading for the selected device
                        </Typography>

                        {widget.widgetType === "gauge" ? (
                          Number.isFinite(Number(widget.rawValue)) ? (
                            <Box
                              sx={{
                                mt: 2.5,
                                p: 2,
                                borderRadius: 4,
                                background: `linear-gradient(180deg, ${widget.color}14 0%, rgba(255,255,255,0.8) 100%)`,
                                border: `1px solid ${widget.color}20`,
                              }}
                            >
                              <LinearProgress
                                variant="determinate"
                                value={Math.max(
                                  0,
                                  Math.min(100, Number(widget.rawValue)),
                                )}
                                sx={{
                                  height: 12,
                                  borderRadius: 999,
                                  backgroundColor: "rgba(148,163,184,0.18)",
                                  "& .MuiLinearProgress-bar": {
                                    borderRadius: 999,
                                    background: `linear-gradient(90deg, ${widget.color}, ${widget.color}CC)`,
                                  },
                                }}
                              />
                            </Box>
                          ) : (
                            <Alert severity="info" sx={{ mt: 2 }}>
                              This KPI cannot be calculated because required inputs are unavailable.
                            </Alert>
                          )
                        ) : widget.widgetType !== "stat" ? (
                          <Box
                            sx={{
                              mt: 2.5,
                              height: 190,
                              p: 1.5,
                              borderRadius: 4,
                              background:
                                "linear-gradient(180deg, rgba(248,250,252,0.96) 0%, rgba(255,255,255,0.96) 100%)",
                              border: "1px solid rgba(226,232,240,0.8)",
                            }}
                          >
                            <DeviceWidgetChart widget={widget} />
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              mt: 2.5,
                              p: 1.6,
                              borderRadius: 4,
                              background: `linear-gradient(180deg, ${widget.color}12 0%, rgba(255,255,255,0.94) 100%)`,
                              border: `1px solid ${widget.color}20`,
                            }}
                          >
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <Typography
                                sx={{
                                  fontSize: 13,
                                  color: "#64748B",
                                  fontWeight: 700,
                                }}
                              >
                                Status band
                              </Typography>
                              <Box
                                sx={{
                                  width: 56,
                                  height: 10,
                                  borderRadius: 999,
                                  background: `linear-gradient(90deg, ${widget.color}, ${widget.color}66)`,
                                }}
                              />
                            </Stack>
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>

                  {(selectedDevice?.widgets || []).length >= MAX_WIDGETS_RENDERED ? (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Showing top {MAX_WIDGETS_RENDERED} widgets. Remaining metrics are available in Raw Telemetry.
                    </Alert>
                  ) : null}

                  <Box sx={{ mt: 2.5 }}>
                    <Typography sx={{ fontSize: 17, fontWeight: 800, color: "#0F172A" }}>
                      Raw Telemetry Snapshot
                    </Typography>
                    <Typography sx={{ mt: 0.4, color: "#64748B", fontSize: 13 }}>
                      All schema and unknown fields remain accessible here.
                    </Typography>

                    {rawTelemetryEntries.length ? (
                      <Box
                        sx={{
                          mt: 1.5,
                          border: "1px solid rgba(226,232,240,0.9)",
                          borderRadius: 3,
                          backgroundColor: "#fff",
                          overflow: "hidden",
                        }}
                      >
                        {rawTelemetryEntries.map(([key, value], index) => (
                          <Stack
                            key={key}
                            direction="row"
                            justifyContent="space-between"
                            spacing={2}
                            sx={{
                              px: 1.5,
                              py: 1,
                              borderBottom:
                                index === rawTelemetryEntries.length - 1
                                  ? "none"
                                  : "1px solid rgba(241,245,249,0.9)",
                            }}
                          >
                            <Typography sx={{ color: "#475569", fontWeight: 700, fontSize: 13 }}>
                              {key}
                            </Typography>
                            <Typography sx={{ color: "#0F172A", fontSize: 13, textAlign: "right" }}>
                              {formatMetricValue(value)}
                            </Typography>
                          </Stack>
                        ))}
                      </Box>
                    ) : (
                      <Alert severity="info" sx={{ mt: 1.5 }}>
                        No telemetry has been received.
                      </Alert>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Stack>
        )}
      </Box>
    </Box>
  );
}

export default DeviceTypeDashboard;













